import type {
  DiscoveredExperience,
  ExperienceDiscoveryPort,
  ExperienceDiscoveryResult,
} from '../../../ports/ExperienceDiscoveryPort';
import { gaiaHttpUrl } from '../url/GaiaUrls';

type RawExperience = Record<string, unknown>;
type RawDiscovery = {
  default_experience_id?: unknown;
  defaultExperienceId?: unknown;
  experience?: unknown;
  experiences?: unknown;
};

export class GaiaExperienceDiscoveryHttp implements ExperienceDiscoveryPort {
  constructor(private baseUrl: string) {}

  async discoverDefaultExperience(token: string): Promise<ExperienceDiscoveryResult> {
    const res = await fetch(gaiaHttpUrl(this.baseUrl, '/api/v0.3/account-bootstrap/experiences/default'), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`Experience discovery failed: ${res.status}`);

    const raw = (await res.json()) as RawDiscovery;
    const experience = normalizeExperience(raw.experience);
    const defaultExperienceId = text(raw.default_experience_id) ?? text(raw.defaultExperienceId) ?? experience.id;
    const experiences = Array.isArray(raw.experiences)
      ? raw.experiences.map(normalizeExperience).filter((item): item is DiscoveredExperience => Boolean(item))
      : [experience];

    return { defaultExperienceId, experience, experiences };
  }
}

function normalizeExperience(value: unknown): DiscoveredExperience {
  if (!isRecord(value)) {
    throw new Error('Experience discovery response missing experience');
  }

  const id = text(value.id) ?? text(value.experience_id) ?? text(value.experienceId);
  if (!id) {
    throw new Error('Experience discovery response missing experience id');
  }

  return {
    id,
    experienceId: text(value.experience_id) ?? text(value.experienceId) ?? id,
    displayName: text(value.display_name) ?? text(value.displayName) ?? id,
    themeKey: text(value.theme_key) ?? text(value.themeKey),
    clientBootstrapRequired: Boolean(value.client_bootstrap_required ?? value.clientBootstrapRequired ?? true),
  };
}

function isRecord(value: unknown): value is RawExperience {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}
